// oxlint-disable typescript/no-explicit-any
import { AxiosProgressEvent, AxiosRequestConfig, AxiosResponse, CancelTokenSource, ResponseType } from 'axios';

import { ActivityResponse as VatixActivityResponse } from 'ui/types';

import { FeedProps } from 'ui/Feed/types';

import { Gender, EntityState, ProtectorType } from 'core/constants';

import { ActivityType } from 'utils/stores/Activities/constants';
import RootStore from 'stores/Root';

import { TaskStatus, TaskState } from './modules/tasks/tasks.constants';

import API from './api';
import { RedAlertResponse } from './modules/alarms/alarms.types';
import { DeviceResponse } from './modules/devices/devices.types';
import { InspectionTaskLinkType } from './modules/workflows/workflows.types';
import { UserNameResponse, UserResponse } from './modules/user/user.types';
import { JSONSchemaType } from './modules/incidents/incidents.types';

export interface RequestOptions {
  baseURL?: string;
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  params?: {
    [k: string]: any;
  };
  data?: {
    [k: string]: any;
  };
  headers?: {
    [k: string]: string;
  };
  authorization?: string;
  timeout?: number;
  signal?: AbortSignal;
  onUploadProgress?: (event: AxiosProgressEvent) => void;
  cancelTokenSource?: CancelTokenSource;
  responseType?: ResponseType;
  background?: boolean;
}

export type BackgroundOption = { background?: boolean };

export interface APIRequest<T> {
  (): Promise<AxiosResponse<T>>;
  config: AxiosRequestConfig;
}

export interface FormErrors {
  nonFieldErrors?: string[];
  fieldErrors?: {
    [k: string]: string[];
  };
}

export interface RowProps<T> {
  rowData: T;
}
export interface UuidableSubject {
  uuid: string;
  subject: string;
}
export interface PaginatedResponse<T> {
  results: T[];
  next: string | null;
  count: number;
  previous: number | null;
}

export interface UuidableName {
  uuid: string;
  name: string;
}

export type SearchResponse = UuidableName;

export interface LocationProperties {
  lng: string;
  lat: string;
  address: string;
}

export type AnswerDetails = { [key: string]: string | string[] | LocationProperties };

export type UploadUserType = {
  firstName: string | null;
  lastName: string | null;
  emailAddress: string | null;
  phoneNumber: string | null;
  role: string | null;
  dateOfBirth: string | null;
  teams: string | null;
  memorableWord: string | null;
  loneWorkingLicence: string;
  auditsLicence: string;
  account?: string | null;
  eventsLicence: string;
  documentsLicence: string;
  costCentre: string | null;
  alarmResponseProfile: string | null;
  jobTitle?: string | null;
  employeeId?: string | null;
  startDate?: string | null;
  country?: string | null;
  department?: string | null;
  region?: string | null;
  employmentType?: string | null;
  site?: string | null;
  manager?: string | null;
  unit?: string | null;
  customField1?: string | null;
  customField2?: string | null;
  customField3?: string | null;
  customField4?: string | null;
  customField5?: string | null;
};

export interface CreatorData extends UuidableName {
  phoneNumber: string;
}

export interface ActivityResponse {
  uuid: string;
  type: ActivityType;
  user: UserResponse | null;
  creator: UserNameResponse | null;
  creatorData: CreatorData | null;
  alarm: RedAlertResponse | null;
  created: string;
  occurred: string;
  message: string;
  status: string;
  device: DeviceResponse | null;
  hasLocation: boolean;
  extra: null | object;
  incident: UuidableName | null;
}

export interface VoiceMemoExtra {
  fileSize: number;
  fileName: string;
}

export interface VoiceMemoActivityResponse extends ActivityResponse {
  type: ActivityType.VoiceMemo;
  user: UserResponse;
  creator: UserNameResponse;
  creatorData: null;
  device: DeviceResponse;
  extra: VoiceMemoExtra;
}

export interface RaisedRedAlertActivityResponse extends VatixActivityResponse {
  alert: RedAlertResponse;
}

export interface UserAddedActivityResponse extends VatixActivityResponse {
  user: UserNameResponse;
}

export interface UserNameGenderResponse extends UserNameResponse {
  gender: Gender | null;
}

export type ActivityListResponse = PaginatedResponse<ActivityResponse>;

export type SeparatorResponse = ActivityResponse;

export type OrganisationResponse = UuidableName;

export type ActivityMapping = FeedProps<RootStore, typeof API>['mapping'];

export interface ProfileOrganisationResponse extends OrganisationResponse {
  showContacts: boolean;
  activityMap: boolean;
  enableThreeYearRecurrence: boolean;
  accountNumber: string;
  maximumSitesAllowed: number;
  maximumAssetsAllowed: number;
  maximumContactsAllowed: number;
  hasEventsLicenses: boolean;
  hasRisksLicenses: boolean;
  scimEnabled: boolean;
  scimMappedFields: string[];
}

export interface SurveyResponse {
  hash: string;
  template: string;
}

export interface MonitoringStats {
  arcMonitored: number;
  selfMonitored: number;
  notMonitored: number;
}

export type ChartType = {
  date: string[];
  active: number[];
  inactive: number[];
};

export type TagResponse = UuidableName;
export interface PaginationParams {
  limit: number;
  offset: number;
}

export interface OrderingParams {
  ordering: string;
}

export interface AsyncResponse<T> {
  data?: T;
  error?: string;
}

export const DisplayProtectorType: { [key in ProtectorType]: string } = {
  [ProtectorType.ShortText]: 'Short Text',
  [ProtectorType.LongText]: 'Long Text',
  [ProtectorType.SingleChoice]: 'Single Choice',
  [ProtectorType.MultiChoice]: 'Multiple Choice',
  [ProtectorType.Date]: 'Date',
  [ProtectorType.User]: 'User',
  [ProtectorType.Location]: 'Location',
  [ProtectorType.Number]: 'Number',
  [ProtectorType.DateTime]: 'Date/Time',
  [ProtectorType.Time]: 'Time',
  [ProtectorType.Image]: 'Image',
  [ProtectorType.Section]: 'Section',
  [ProtectorType.Lookup]: 'Lookup',
  [ProtectorType.RiskFactor]: 'Risk matrix',
  [ProtectorType.Pipeline]: 'Pipeline',
  [ProtectorType.Stage]: 'Stage',
  [ProtectorType.Attachment]: 'Attachment',
  [ProtectorType.Signature]: 'Signature',
  [ProtectorType.RepeatingGroup]: 'Repeating group',
  [ProtectorType.YesNoNa]: 'Yes/No/N-A',
};

// Protector types that do not have properties = all types expect multi choice, single choice, and lookup
export const ProtectorTypesWithoutProperties = [
  ProtectorType.ShortText,
  ProtectorType.LongText,
  ProtectorType.Date,
  ProtectorType.User,
  ProtectorType.Location,
  ProtectorType.Number,
  ProtectorType.DateTime,
  ProtectorType.Time,
  ProtectorType.Pipeline,
  ProtectorType.Stage,
  ProtectorType.Attachment,
  ProtectorType.RepeatingGroup,
];

export type LocationAnswerType = {
  lat: number;
  lng: number;
  address?: string;
};

interface DefinitionType {
  description: string;
  type: 'string';
}

export interface TemplateContent {
  mandatory: string[];
  order: string[];
  properties: { [key: string]: DefinitionType };
}

export type QueryParams = { [k: string]: string | number | boolean };

export interface StartDateSearchResponse {
  startDate: string;
}

export enum CSVImportColumns {
  firstName = 'First Name',
  lastName = 'Last Name',
  email = 'Email address',
  role = 'Role',
  dateOfBirth = 'Date of birth',
  phoneNumber = 'Phone Number',
  teams = 'Teams',
  memorableWord = 'Memorable Word',
  loneWorkingLicense = 'Lone Working Licence',
  auditsLicense = 'Audits Licence',
  eventsLicense = 'Events Licence',
  documentsLicense = 'Documents Licence',
  alarmResponseProfile = 'Alarm Response Profile',
  costCentre = 'Cost Centre',
  jobTitle = 'Job title',
  employeeId = 'Employee ID',
  startDate = 'Start date',
  country = 'Country',
  site = 'Associated site',
  manager = 'Line manager',
  unit = 'Unit',
}

export type AuditTaskLinkHost = 'entity' | 'draft' | 'submission' | 'legacy_submission';

/** `question` is the label, resolved on retrieve only — the list omits it, so fall back to the id. */
export type AuditTaskLinkExtraData = {
  questionId: string;
  question: string | null;
  host: AuditTaskLinkHost;
};

export type LinksType = {
  entity: string;
  instance:
    | {
        uuid: string;
        name: string;
        reporter: {
          uuid: string;
          name: string;
        };
        submitted: string;
        isActive: boolean;
      }
    | UuidableName;
  extraData?: InspectionTaskLinkType | UuidableSubject | AuditTaskLinkExtraData;
};

export interface TemplateSearchResponse extends UuidableName {
  lastTemplateVersion: string;
}

export interface EventStateActivityResponse extends VatixActivityResponse {
  state: EntityState;
}

export interface EventOwnerChangeActivityResponse extends VatixActivityResponse {
  owner: UserNameResponse | null;
  previousOwner: UserNameResponse | null;
  source: string | null;
}

export interface EventNameChangeActivityResponse extends VatixActivityResponse {
  site: UuidableName;
  name: string | null;
  previousName: string | null;
}

export interface StateActivityResponse extends VatixActivityResponse {
  state: TaskState;
  task: UuidableName;
}

export interface SiteStateActivityResponse extends VatixActivityResponse {
  state: EntityState;
  site: UuidableName;
}

export interface SiteManagerChangeActivityResponse extends VatixActivityResponse {
  site: UuidableName;
  manager: UserNameResponse | null;
  previousManager: UserNameResponse | null;
}

export interface SiteNameChangeActivityResponse extends VatixActivityResponse {
  site: UuidableName;
  name: string | null;
  previousName: string | null;
}

export interface StatusActivityResponse extends VatixActivityResponse {
  status: TaskStatus;
}

export interface AssignmentActivityResponse extends VatixActivityResponse {
  user: UserNameResponse | null;
  previousUser: UserNameResponse | null;
}

export interface FieldResponse {
  path: string[];
  schema: JSONSchemaType;
}

export type SearchRequest = Partial<{
  query: string;
  limit: number;
  offset: number;
}>;

export interface Licenses {
  workflows: Boolean;
  protectorNotify: Boolean;
  protectorAlarms: Boolean;
  // we need to keep this type, for user creation
  protectorIncidents: Boolean;
  // Optional because this interface has never declared every licence the creation payload carries
  // (events, documents and riskAssessments are also sent untyped). Declaring risks stops the one
  // field a Risk seat depends on from going out unchecked.
  risks?: Boolean;
}
